import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { siteInfo } from '@/mocks/site';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '北京',
      streetAddress: siteInfo.address,
    },
    foundingDate: siteInfo.founded,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '国产在线一区是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '国产在线一区是一个高清国产视频聚合平台，汇聚海量高清国产视频内容，涵盖电影、电视剧、综艺、纪录片、动漫等多个品类，提供流畅在线播放体验，无需下载即可观看。',
        },
      },
      {
        '@type': 'Question',
        name: '国产在线一区是否需要付费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '国产在线一区大部分内容免费观看，用户可以自由浏览海量国产高清视频。我们致力于为广大用户提供优质的免费观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '国产在线一区支持哪些设备？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '国产在线一区支持PC电脑、平板、手机等多种设备访问，无需安装任何插件或APP，打开浏览器即可享受高清观影体验，真正实现随时随地想看就看。',
        },
      },
      {
        '@type': 'Question',
        name: '国产在线一区的内容更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '国产在线一区内容持续更新，每日都有新内容上线。我们紧跟热门影视动态，确保用户能够第一时间看到最新的国产视频内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系国产在线一区？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以通过以下方式联系我们：邮箱 ${siteInfo.email}，电话 ${siteInfo.phone}，地址：${siteInfo.address}。我们的客服团队将竭诚为您服务。`,
        },
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`关于我们 - ${siteInfo.name}`}
        description={`了解${siteInfo.fullName}的品牌故事、发展历程、口碑荣誉和联系方式。${siteInfo.description}`}
        keywords="国产在线一区,关于国产在线一区,国产视频平台介绍,国产在线一区联系方式"
        canonicalUrl={`${siteInfo.url}/about`}
        ogType="website"
        schema={[organizationSchema, faqSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Page Header */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto text-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于{siteInfo.name}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                {siteInfo.tagline}
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 items-start">
                <div className="lg:w-1/2">
                  <div className="w-full aspect-[4/3] rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=modern%20creative%20office%20workspace%20with%20large%20screens%20displaying%20video%20content%2C%20warm%20ambient%20lighting%2C%20collaborative%20team%20environment%2C%20contemporary%20Chinese%20tech%20company%20aesthetic%2C%20clean%20minimalist%20design&width=800&height=600&seq=about-brand-001&orientation=landscape"
                      alt="国产在线一区办公环境"
                      title="国产在线一区 - 品牌介绍"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="lg:w-1/2">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    品牌故事
                  </h2>
                  <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
                    {siteInfo.brandStory}
                  </p>
                  <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
                    我们始终坚持以用户为中心，不断优化产品体验，丰富内容库，致力于让每一位用户都能在这里找到自己喜欢的国产视频内容。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Grid */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                平台数据
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: siteInfo.usersCount, label: '累计用户', icon: 'ri-user-heart-line' },
                  { value: siteInfo.dailyActive, label: '日活跃用户', icon: 'ri-flashlight-line' },
                  { value: siteInfo.videoCount, label: '视频总量', icon: 'ri-film-line' },
                  { value: siteInfo.teamSize, label: '团队成员', icon: 'ri-team-line' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${stat.icon} text-xl text-accent-600`}></i>
                    </div>
                    <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                      {stat.value}
                    </p>
                    <p className="text-xs md:text-sm text-foreground-500 whitespace-nowrap">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Honors Section */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                荣誉口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {siteInfo.honors.map((honor, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-4 p-5 bg-background-100 rounded-lg border border-background-200/70"
                  >
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                      <i className="ri-award-line text-lg text-accent-600"></i>
                    </div>
                    <span className="text-sm md:text-base text-foreground-800 font-medium">{honor}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex flex-col items-center text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-mail-line text-xl text-primary-600"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-800 mb-2 whitespace-nowrap">电子邮箱</h4>
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="text-sm text-foreground-600 hover:text-primary-500 transition-colors break-all cursor-pointer"
                  >
                    {siteInfo.email}
                  </a>
                </div>
                <div className="flex flex-col items-center text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-phone-line text-xl text-primary-600"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-800 mb-2 whitespace-nowrap">联系电话</h4>
                  <span className="text-sm text-foreground-600 whitespace-nowrap">{siteInfo.phone}</span>
                </div>
                <div className="flex flex-col items-center text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-map-pin-line text-xl text-primary-600"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-800 mb-2 whitespace-nowrap">公司地址</h4>
                  <span className="text-sm text-foreground-600 leading-relaxed">{siteInfo.address}</span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </div>
    </>
  );
}